<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <card
            class="strpied-tabled-with-hover"
            body-classes="table-full-width table-responsive"
          >
            <template v-slot:header>
              <h4 class="card-title">{{ title }}</h4>
              <p class="card-category">{{ description }}</p>
            </template>

            <div class="row mt-3">
              <div class="col-md-12 p-0">
                <div class="row px-3">
                  <!-- Skeleton Loading Cards -->
                  <template v-if="isBusy && Platforms.length === 0">
                    <div v-for="i in 3" :key="'skeleton-' + i" class="col-12 col-md-6 col-lg-4 mb-4">
                      <div class="skeleton-card shadow-sm border-0 h-100 d-flex flex-column">
                        <div class="skeleton-card-image"></div>
                        <div class="skeleton-card-body p-4">
                          <div class="skeleton-card-title mb-3"></div>
                          <div class="skeleton-card-text mb-2"></div>
                          <div class="skeleton-card-text mb-2"></div>
                          <div class="skeleton-card-actions mt-4"></div>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Real Cards -->
                  <template v-else>
                    <div v-for="platform in Platforms" :key="platform.id" class="col-12 col-md-6 col-lg-4 mb-4">
                      <div class="postulation-card shadow-sm border-0 h-100 d-flex flex-column">
                      <!-- Image Section -->
                      <div class="position-relative overflow-hidden" style="border-radius: 15px 15px 0 0; background: #f8f9fa;">
                        <img
                          :src="platform.platform?.url_banner || platform.platform?.url_image || '/img/ccdlogolite.png'"
                          class="card-img-top"
                          alt="Platform logo"
                          style="height: 180px; object-fit: cover; width: 100%;"
                          @error="handleImageError($event)"
                        />
                      </div>

                      <div class="card-body d-flex flex-column p-4">
                        <div class="mb-1">
                          <h5 class="card-title fw-bold text-dark mb-1" style="font-size: 1.1rem; line-height: 1.4; min-height: 3rem;">
                            {{ platform.platform?.name || 'Postulación sin nombre' }}
                          </h5>
                          
                          <!-- Fila 1: Estatus de Postulación y Pago -->
                          <div class="row mt-2 mb-2">
                            <div class="col-6">
                              <div class="mb-2">
                                <small class="text-muted d-block mb-1" style="font-size: 0.7rem; font-weight: 600;">Estatus de Postulación:</small>
                                <span
                                  class="payment-status-tag"
                                  :class="getPaymentStatusBadgeClass(platform.id_type_status)"
                                >
                                  {{ platform.type_status?.name || 'Pendiente' }}
                                </span>
                              </div>
                            </div>
                            <div v-if="platform.type_status_payment" class="col-6">
                              <div class="mb-2">
                                <small class="text-muted d-block mb-1" style="font-size: 0.7rem; font-weight: 600;">Estatus de Pago:</small>
                                <span
                                  class="payment-status-tag"
                                  :class="getPaymentStatusBadgeClass(platform.id_type_status_payment)"
                                >
                                  <font-awesome-icon :icon="['fas', 'dollar-sign']" class="me-1" style="font-size: 11px;" />
                                  {{ platform.type_status_payment?.name || 'Pago' }}
                                </span>
                              </div>
                            </div>
                          </div>

                          <!-- Fila 2: Información Financiera -->
                          <div class="row mb-0">
                            <div class="col-12">
                              <div class="p-2" style="background-color: #f8f9fa; border-radius: 8px;">
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                  <small class="fw-bold"style="font-size: 0.65rem; color: #6c757d;">Monto de inversión:</small>
                                  <span style="font-size: 0.75rem; font-weight: 600; color: #212529;">${{ parseFloat(platform.amount || 0).toFixed(2) }}</span>
                                </div>
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                  <small class="fw-bold"style="font-size: 0.65rem; color: #28a745;">Total abonado:</small>
                                  <span style="font-size: 0.75rem; font-weight: 600; color: #28a745;">${{ parseFloat(platform.total_paid || 0).toFixed(2) }}</span>
                                </div>
                                <div class="d-flex justify-content-between align-items-center">
                                  <small class="fw-bold"style="font-size: 0.65rem; color: #dc3545;">Total pendiente:</small>
                                  <span style="font-size: 0.75rem; font-weight: 600; color: #dc3545;">${{ parseFloat(platform.total_pending || 0).toFixed(2) }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Action Buttons -->
                        <div class="mt-2 d-flex justify-content-between gap-2 pt-3 border-top">
                          <button
                            class="btn btn-sm action-btn btn-view flex-grow-1"
                            @click="openUploadElement(platform)"
                            title="Ver detalles"
                          >
                            <font-awesome-icon :icon="['fas', 'eye']" style="font-size: 14px;" />
                            <span>{{ platform.id_type_status == 3 ? ' Ver postulación' : ' Ver requisitos' }}</span>
                          </button>
                          <button
                            class="btn btn-sm action-btn btn-payment flex-grow-1"
                            :style="platform.id_type_status === 1 ? 'opacity: 0.5; filter: grayscale(1);' : ''"
                            :disabled="platform.id_type_status === 1"
                            @click="openModalPayment(platform)"
                            title="Reportar pago"
                          >
                            <font-awesome-icon :icon="['fas', 'money-bill-wave']" style="font-size: 14px;" />
                            <span> Pago</span>
                          </button>
                          <button
                            class="btn btn-sm action-btn btn-delete px-3"
                            @click="deleteStatus(platform)"
                            title="Eliminar"
                          >
                            <font-awesome-icon :icon="['fas', 'trash']" style="font-size: 14px;" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  </template>

                  <div v-if="Platforms.length === 0 && !isBusy" class="col-12 text-center py-5">
                    <font-awesome-icon :icon="['fas', 'folder-open']" style="font-size: 40px; color: #ccc;" />
                    <p class="mt-3 text-muted">No tienes postulaciones registradas actualmente.</p>
                  </div>
                </div>

                <!-- MODAL UPLOAD IMAGE -->
                <BaseModal
                  :show="modalActiveUpdate"
                  modal-id="modalPostulation"
                  @close="closeUploadElement"
                  body-class="p-4 modal-body-scroll"
                  size="lg"
                >
                  <template #header>
                    <h2
                      class="modal-title w-100 text-center m-0 text-white"
                    >
                      Requisitos
                    </h2>
                  </template>

                  <template #default>
                    <RequirementsForm
                      ref="requirementsForm"
                      :key="form_update.id"
                      :platform="form_update.platform"
                      :files-user="filesUser"
                      :id-type-status="form_update.id_type_status"
                      :url-file="form_save.url_file"
                      @update:url-file="form_save.url_file = $event"
                      @delete-file="deleteFile"
                      @upload-files="handleUploadFiles"
                    />
                  </template>

                  <template #footer v-if="form_update.id_type_status == 1 || form_update.id_type_status == 2">
                    <button
                      type="button"
                      class="btn btn-sm btn-info btn-fill px-4"
                      @click.prevent="handleUploadFilesFromModal"
                    >
                      Subir
                    </button>
                  </template>
                </BaseModal>

                <!-- MODAL PAYMENT REPORT -->
                <BaseModal
                  :show="selectorOpen"
                  modal-id="modalPayment"
                  size="xl"
                  @close="closePaymentElement"
                  :content-style="{ borderRadius: '15px', border: 'none', overflow: 'hidden' }"
                  body-class="p-0"
                  :body-style="{ overflowY: 'auto', background: 'white', maxHeight: '82vh !important' }"
                >
                  <template #default>
                    <PaymentReportForm
                      v-if="selectorOpen"
                      ref="paymentReportForm"
                      :key="form_update.id"
                      :model-value="form_payment"
                      @update:model-value="Object.assign(form_payment, $event)"
                      @update:bcvRate="bcvRate = $event"
                      :course-name="form_update.platform.name"
                      :total-paid="form_update.total_paid"
                      :total-pending="form_update.total_pending"
                      :fallback-amount="form_update.course_amount"
                      :id-platform-offer="form_update.id_platform_offer"
                      :id-platform="form_update.id_platform"
                      @submit="(file) => savePayment(file)"
                      @cancel="closePaymentElement"
                    />

                  </template>
                </BaseModal>
              </div>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUpdated, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from "sweetalert2";
import "sweetalert2/src/sweetalert2.scss";
import Card from "src/components/Cards/Card.vue";
import "vue-multiselect/dist/vue-multiselect.min.css";
import BaseModal from "src/components/common/BaseModal.vue";
import RequirementsForm from "src/components/Postulation/RequirementsForm.vue";
import PaymentReportForm from "src/components/Postulation/PaymentReportForm.vue";

import { useUserFilePlatformAPI } from "src/composables/api/useUserFilePlatformAPI.js";
import { useUserAPI } from "src/composables/api/useUserAPI.js";
import { parseEsNumber } from "src/utils/numberUtils.js";
import { formatDate } from "src/utils/dateUtils.js";
import { handleImageError } from "src/utils/imageUtils.js";
import { getStatusBadgeClass, getPaymentStatusBadgeClass } from "src/utils/badgeUtils.js";

const router = useRouter();
const route = useRoute();

// Use composables
const { getUserFiles, uploadFiles, deleteUserFile } = useUserFilePlatformAPI();
const { getUserPlatforms, deleteUserPlatform, createPaymentReport } = useUserAPI();

// State
const title = ref("Mis postulaciones");
const description = ref("Podrás gestionar tus postulaciones registradas en el sistema");
const selectorOpen = ref(false);
const finder = ref("");
const metaInfo = ref({});
const isBusy = ref(false);
const modalActiveUpdate = ref(false);
const isLoadingModal = ref(false);
const idTmp = ref(0);
const requirementsForm = ref(null);
const filesUser = ref([]);
const bcvRate = ref({});
const totalPaymentAmount = ref(0);

const form_payment = reactive({
  selectedCompanyBank: null,
  paymentMethod: 'pago_movil',
  phone: '',
  paymentDate: '',
  currency: 'VES',
  currencyOptions: [
    { code: "VES", name: "Bolívares (Bs)", symbol: "Bs." },
    { code: "USD", name: "Dólares ($)", symbol: "$" },
  ],
  originBank: null,
  reference: '',
  amountWithPrefix: 0,
  urlImage: '',
  id_company_bank: "",
  id_bcv_rate: "",
});


const form_update = reactive({
  id: 0,
  name: "",
  url: "",
  url_banner: "",
  url_image: "",
  description: "",
  autoapproved: "",
  platform: {
    name: "",
    amount: 0,
  },
  requires: "",
  id_type_status: 0,
  id_type_status_payment: "",
  id_platform_offer: null,
});

const form_save = reactive({
  url_file: "",
});


const Platforms = ref([]);
const stockTmp = ref([]);




const loadUserFiles = async (data) => {
  filesUser.value = await getUserFiles(data);
};

const loadElements = async () => {
  const params = {};
  if (metaInfo.value.current_page > 1) {
    params.page = metaInfo.value.current_page;
  }
  if (finder.value !== "") {
    params.query = finder.value;
    params.page = 1;
  }
  isBusy.value = true;
  const result = await getUserPlatforms(params);
  isBusy.value = false;
  Platforms.value = result.data;
  metaInfo.value = result.meta;
  stockTmp.value = result.data;
};

const savePayment = async (file) => {
  const originBankCode =
    form_payment.originBank && typeof form_payment.originBank === "object"
      ? (form_payment.originBank?.code ||
          form_payment.originBank?.bank_code ||
          form_payment.originBank?.id_bank ||
          "")
      : (form_payment.originBank || "");

  const success = await createPaymentReport({
    payment_report_id: form_update.id,
    id_company_bank: form_payment.selectedCompanyBank?.id,
    id_bcv_rate: bcvRate.value.id,
    reference: form_payment.reference,
    payment_method: form_payment.paymentMethod,
    origin_bank: form_payment.originBank?.name || form_payment.originBank || "",
    origin_bank_code: originBankCode,
    amount: parseFloat(form_payment.amountWithPrefix).toFixed(2),
    date: form_payment.paymentDate,
    currency: form_payment.currency,
    phone: form_payment.paymentMethod === 'pago_movil' ? form_payment.phone : undefined,
    url_image: file,
  });

  if (success) {
    closePaymentElement();
    await loadElements();
  }
};

const handleUploadFiles = async (filePondInstance) => {
  const success = await uploadFiles(filePondInstance, form_update.id);
  if (success) {
    closeUploadElement();
    await loadUserFiles(form_update);
  }
};

const handleUploadFilesFromModal = () => {
  requirementsForm.value?.uploadFiles();
};

const deleteFile = async (fileId) => {
  const success = await deleteUserFile(fileId);
  if (success) {
    await loadUserFiles(form_update);
  }
};

const deleteStatus = async (data) => {
  const success = await deleteUserPlatform(data);
  if (success) {
    await loadElements();
  }
};

const openUploadElement = async (data) => {
  if (data.id_type_status == 3 && data.id_type_status_payment == 3) {
    Swal.fire({
      title: "Postulación aprobada",
      text: "Ya puedes acceder a la curso",
      icon: "success",
      showConfirmButton: true,
    });
    router.push({ path: "platform/" + data.id_platform });
  } else if (
    data.id_type_status == 3 &&
    (data.id_type_status_payment == 1 || data.id_type_status_payment == 0)
  ) {
    Swal.fire({
      title: "Postulación aprobada",
      text: "Ya puedes realizar el pago para acceder a la curso",
      icon: "success",
      showConfirmButton: true,
    });
    router.push({ path: "platform/" + data.id_platform });
  } else if (data.id_type_status == 3 && data.id_type_status_payment == 2) {
    Swal.fire({
      title: "Hubo un problema con tu pago",
      text: "Parece que tu pago fue rechazado, por favor, cree un nuevo reporte de pago y chequee que los datos son correctos para acceder a la curso",
      icon: "warning",
      showConfirmButton: true,
    });
  } else if (data.id_type_status == 3 && data.id_type_status_payment == 4) {
    Swal.fire({
      title: "Hubo un problema con tu pago",
      text: "Parece que tu pago estaba incompleto, por favor, pague el diferencial para acceder a la curso",
      icon: "warning",
      showConfirmButton: true,
    });
  } else {
    await loadUserFiles(data);
    Object.assign(form_update, data);
    idTmp.value = data.id;
    modalActiveUpdate.value = true;
  }
};

const closeUploadElement = () => {
  modalActiveUpdate.value = false;
};

const openModalPayment = async (data) => {
  Object.assign(form_update, data);
  idTmp.value = data.id;
  isLoadingModal.value = true;
  selectorOpen.value = true;

  // Reset form_payment to initial state
  form_payment.selectedCompanyBank = null;
  form_payment.paymentMethod = 'pago_movil';
  form_payment.phone = '';
  form_payment.paymentDate = '';
  form_payment.currency = 'VES';
  form_payment.originBank = null;
  form_payment.reference = '';
  form_payment.amountWithPrefix = 0;
  form_payment.urlImage = '';

  if (data && data.platform) {
    form_update.platform.amount = data.amount;
    form_update.platform.name = data.platform.name || form_update.platform.name;
    form_update.course_amount =
      data.course_amount !== undefined && data.course_amount !== null
        ? data.course_amount
        : (data.amount !== undefined && data.amount !== null
            ? data.amount
            : form_update.course_amount);
  }

  const numericAmount = parseEsNumber(form_update.course_amount || 0);
  const numericBcv = parseEsNumber(bcvRate.value.value);
  totalPaymentAmount.value = numericAmount * numericBcv;
  isLoadingModal.value = false;
};

watch(bcvRate, (newValue) => {
  if (newValue && newValue.value) {
    const numericAmount = parseEsNumber(form_update.course_amount || 0);
    const numericBcv = parseEsNumber(newValue.value);
    totalPaymentAmount.value = numericAmount * numericBcv;
  }
});

const closePaymentElement = () => {
  Object.assign(form_payment, {
    id_company_bank: "",
    id_bcv_rate: "",
    reference: "",
    origin_bank: "",
    amount: 0,
    date: "",
    url_image: "",
    currency: "VES",
    payment_method: "pago_movil",
    phone: "",
  });
  selectorOpen.value = false;

  if (route.query.openPayment || route.query.id_platform) {
    const { openPayment, id_platform, ...remainingQuery } = route.query;
    router.replace({ query: remainingQuery });
  }
};

// Lifecycle hooks
onMounted(async () => {
  await loadElements();

  if (route.query.openPayment === '1' && route.query.id_platform) {
    const match = Platforms.value.find(
      (p) => Number(p.id_platform) === Number(route.query.id_platform)
    );
    if (match) {
      openModalPayment(match);
    }
  }
});

</script>
<style scoped>
.disable-scroll {
  overflow: visible;
}

.skeleton-line {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-card {
  background-color: #fff;
  border-radius: 15px;
  overflow: hidden;
}

.skeleton-card-image {
  height: 180px;
  background-color: rgba(0, 0, 0, 0.05);
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-card-body {
  background-color: #fff;
}

.skeleton-card-title {
  height: 3rem;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-card-text {
  height: 16px;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  width: 60%;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-card-actions {
  height: 40px;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
}
</style>
<style>
.postlation_table {
  min-width: 800px;
}
.postlation_table td:first-child {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 120px;
  width: 120px;
}
.postlation_table td:nth-child(2) {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 300px;
  width: 300px;
}
.postlation_table td:nth-child(3) {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 120px;
  width: 120px;
}
.postlation_table td:nth-child(4) {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 300px;
  width: 300px;
}

.postlation_table td:last-child {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  justify-content: center;
  max-width: 200px;
  width: 200px;
}

.button-search {
  height: 100%;
  margin-left: 5px;
}

.my-table th:last-child {
  width: 35%;
}

.table-container {
  width: auto;
  max-width: 3000px;
  /* ajusta el valor al ancho máximo que desees */
  overflow-x: auto;
  align-items: center;
  text-align: center;
}

.l-table {
  width: 100%;
  table-layout: fixed;
}

.l-table td,
.l-table th {
  white-space: nowrap;
}

.my-button {
  margin-left: 10px;
}

.popup {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  border: 1px solid black;
  padding: 10px;
}

.custom-rounded-multiselect .multiselect__tags {
  border-radius: 2rem;
}

.custom-rounded-multiselect .multiselect__tags .multiselect__tag {
  border-radius: 2rem;
}

.custom-placeholder::placeholder {
  font-size: 14px;
  color: #cfcfca;
  display: inline-block;
  font-weight: 600;
}
.modal-body-scroll {
  max-height: 70vh;
  overflow-y: auto;
}

.custom-modal-payment .btn.custom-btn-main {
  background: #6d263a;
  color: #fff;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 0.8rem 0;
  width: 100%;
  box-shadow: 0 2px 8px rgba(109, 38, 58, 0.08);
  transition: background 0.2s;
}

.custom-modal-payment .btn.custom-btn-main:hover {
  background: #a14e5a;
}

/* Postulation Cards Styles */
.postulation-card {
  border-radius: 15px;
  background: #fff;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid rgba(180, 155, 87, 0.15);
  overflow: hidden;
}

.postulation-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 35px rgba(53, 16, 32, 0.15) !important;
  border-color: rgba(180, 155, 87, 0.4);
}

.status-badge {
  padding: 6px 14px;
  border-radius: 30px;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  color: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  letter-spacing: 0.5px;
}

.card-status-container {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
}

.badge-pending { background-color: #f39c12 !important; }
.badge-approved { background-color: #27ae60 !important; }
.badge-rejected { background-color: #D32F2F !important; }
.badge-info { background-color: #3498db !important; }
.badge-secondary { background-color: #95a5a6 !important; }

/* Payment Status Tag (Separated) */
.payment-status-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  border-left: 3px solid transparent;
}

.payment-tag-pending { 
  background-color: rgba(230, 126, 34, 0.1); 
  color: #e67e22;
  border-left-color: #e67e22;
}
.payment-tag-paid { 
  background-color: rgba(46, 204, 113, 0.1); 
  color: #2ecc71;
  border-left-color: #2ecc71;
}
.payment-tag-rejected { 
  background-color: rgba(192, 57, 43, 0.1); 
  color: #c0392b;
  border-left-color: #c0392b;
}
.payment-tag-process { 
  background-color: rgba(155, 89, 182, 0.1); 
  color: #9b59b6;
  border-left-color: #9b59b6;
}

.action-btn {
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  padding: 8px 12px;
  border: 1px solid transparent;
}

.btn-view {
  background-color: rgba(180, 155, 87, 0.1);
  color: #b49b57;
  border-color: rgba(180, 155, 87, 0.2);
}

.btn-view:hover {
  background-color: #b49b57;
  color: white;
  transform: scale(1.03);
}

.btn-payment {
  background-color: rgba(53, 16, 32, 0.05);
  color: #351020;
  border-color: rgba(53, 16, 32, 0.1);
}

.btn-payment:hover {
  background-color: #351020;
  color: white;
  transform: scale(1.03);
}

.btn-delete {
  background-color: rgba(206, 13, 13, 0.05);
  color: #ce0d0d;
  border-color: rgba(206, 13, 13, 0.1);
}

.btn-delete:hover {
  background-color: #ce0d0d;
  color: white;
  transform: scale(1.03);
}
</style>
